<script setup lang="ts">
import { Head, useForm } from '@inertiajs/vue3'
import { onMounted } from 'vue'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import { Button, Input, Card, CardHeader, CardTitle, CardContent } from '@/Components/ui'

interface BikeOption { id: number; slug: string; name: string }

const props = defineProps<{ bikes: BikeOption[]; preselect_id: number | null }>()

const form = useForm({
    product_id: props.preselect_id ?? null as number | null,
    customer_name: '',
    email: '',
    phone: '',
    preferred_at: '',
    customer_notes: '',
})

onMounted(() => {
    if (props.preselect_id && !form.product_id) form.product_id = props.preselect_id
})

function submit() {
    form.post('/probefahrt')
}

const minDate = new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16)
</script>

<template>
    <Head :title="$t('Probefahrt anfragen')" />
    <PublicLayout>
        <section class="px-6 py-10 lg:px-12">
            <div class="mx-auto max-w-2xl">
                <h1 class="text-3xl font-bold">{{ $t('Probefahrt anfragen') }}</h1>
                <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">{{ $t('Such dir ein Bike aus, schlag einen Termin vor – Mike bestätigt zeitnah.') }}</p>

                <Card class="mt-6">
                    <CardContent class="p-6">
                        <form @submit.prevent="submit" class="space-y-4">
                            <div>
                                <label class="mb-1 block text-xs font-medium">{{ $t('Bike *') }}</label>
                                <select v-model.number="form.product_id" required class="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                                    <option :value="null" disabled>{{ $t('– bitte wählen –') }}</option>
                                    <option v-for="b in bikes" :key="b.id" :value="b.id">{{ b.name }}</option>
                                </select>
                            </div>
                            <div>
                                <label class="mb-1 block text-xs font-medium">{{ $t('Wunschtermin *') }}</label>
                                <Input v-model="form.preferred_at" type="datetime-local" :min="minDate" required />
                            </div>
                            <div class="grid gap-3 md:grid-cols-2">
                                <div>
                                    <label class="mb-1 block text-xs font-medium">{{ $t('Name') }} *</label>
                                    <Input v-model="form.customer_name" required />
                                </div>
                                <div>
                                    <label class="mb-1 block text-xs font-medium">{{ $t('Telefon') }}</label>
                                    <Input v-model="form.phone" />
                                </div>
                            </div>
                            <div>
                                <label class="mb-1 block text-xs font-medium">{{ $t('E-Mail') }} *</label>
                                <Input v-model="form.email" type="email" required />
                            </div>
                            <div>
                                <label class="mb-1 block text-xs font-medium">{{ $t('Anmerkungen') }}</label>
                                <textarea v-model="form.customer_notes" rows="3" class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100" :placeholder="$t('Körpergröße, Erfahrung, Wünsche…')" />
                            </div>
                            <Button type="submit" size="lg" class="w-full" :disabled="form.processing">{{ $t('Anfrage senden') }}</Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </section>
    </PublicLayout>
</template>
